import { gql, NetworkStatus, useQuery } from '@apollo/client'

import {
  ItemDescriptionFragmentDoc,
  ItemInformationQuery,
  ItemInformationQueryVariables,
  PortfolioConnectionFragmentDoc,
  PortfolioDetailQuery,
  PortfolioDetailQueryVariables,
  SerialItemOwnersFragmentDoc,
  UserInfoFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { convertPortfolioItemToSku, decodeSkuId, setItemSku } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{
    itemId: string
    userId?: string
    skuId?: string
  }>()
  const { itemId, userId } = router.query
  const skuId = decodeSkuId(router.query.skuId ?? '')

  const { data, fetchMore, networkStatus } = useQuery<
    ItemInformationQuery,
    ItemInformationQueryVariables
  >(QUERY, { variables: { id: itemId, userId } })

  const { data: portfolio } = useQuery<
    PortfolioDetailQuery,
    PortfolioDetailQueryVariables
  >(PORTFOLIO, { variables: { id: itemId, userId: userId } })

  // シリアル一覧。skuIdが指定されている場合は、そのskuIdのシリアルのみを表示する。
  const serials = portfolio?.portfolio?.serials?.filter(({ serial }) => {
    if (!skuId) return true

    return serial?.sku?.id === skuId
  })

  return {
    state: {
      loading: networkStatus === NetworkStatus.loading,
      fetching: networkStatus === NetworkStatus.fetchMore,
      userId,
      user: data?.user ?? null,
      number:
        convertPortfolioItemToSku(portfolio?.portfolio, router.query.skuId)
          ?.number ?? 0,
      serials: serials ?? (null as any),
      hasNextPage: !!data?.item?.serials?.pageInfo?.hasNextPage,
      item: setItemSku(data?.item, skuId),
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            id: itemId,
            userId: userId,
            pageInput: {
              cursor: data?.item?.serials?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ItemInformation(
    $id: ID!
    $userId: ID
    $pageInput: PageInput
    $skuId: String
  ) {
    item(id: $id) {
      ...ItemDescription
      serials(pageInput: $pageInput) {
        ...SerialItemOwners
      }
      itemObtainedCount
    }
    user(id: $userId) {
      ...UserInfo
    }
    #use alias for get user logged in
    userLoggedIn: user {
      id
    }
  }
  ${ItemDescriptionFragmentDoc}
  ${SerialItemOwnersFragmentDoc}
  ${UserInfoFragmentDoc}
`

const PORTFOLIO = gql`
  query PortfolioDetail($id: ID!, $userId: String, $skuId: String) {
    portfolio(id: $id, userId: $userId) {
      ...PortfolioConnection
    }
  }
  ${PortfolioConnectionFragmentDoc}
`
